/**
 * 📊 MONITOR DE SUSTENTAÇÃO DE GANHOS - Dashboard Premium
 * Componente C-Level para visualização de ROI e sustentação de melhorias
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart, ReferenceLine
} from 'recharts';
import {
  AlertTriangle, CheckCircle2, Target, AlertCircle, Plus, Link2
} from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import GainsMonitorService from '../services/gainsMonitorService';
import { SnapshotForm } from './SnapshotForm';
import { KaizenResultSection } from './kaizen/KaizenResultSection';
import { KAIZEN_IMPROVEMENT_TYPE_LABEL } from './ops/kaizen/opsFinancialDerivations';
import {
  annualBenefitLabel,
  benefitClassLabel,
  benefitInclusion,
  canonicalAnnualFinance,
  formatCanonicalPercent,
  formatPaybackMonths,
  inclusionNote,
} from './ops/kaizen/benefitGovernance';
import { openAlertsDetail, openAlertsHeadline, type OpenAlertsRead } from '../utils/regressionAlertState';
import {
  useEligibleKaizenSnapshots,
  useKaizenResultValidations,
} from '../hooks/useKaizenResultValidations';
import { RESULT_UI_LABEL, resultUiState } from '../utils/kaizenResultValidation';
import { useTheme } from '../context/ThemeContext';
import type {
  GainsDashboardData,
  KaizenRecord,
  TrendChartData,
  KaizenROI
} from '../types/gainsMonitor';

// ============================================================================
// CORES DO TEMA
// ============================================================================
const readThemeColor = (name: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const useGainsColors = () => {
  const { theme } = useTheme();
  const colors = React.useMemo(() => ({
    success: readThemeColor('--risk-ok', '#12B76A'),
    warning: readThemeColor('--risk-alert', '#F79009'),
    danger: readThemeColor('--risk-critical', '#F04438'),
    primary: readThemeColor('--brand-primary', '#175CD3'),
    secondary: readThemeColor('--text-secondary', '#475467'),
    gray: readThemeColor('--text-muted', '#98A2B3'),
    borderStrong: readThemeColor('--border-strong', '#D0D5DD'),
    borderSoft: readThemeColor('--border-soft', '#E4E7EC'),
    surface1: readThemeColor('--bg-surface-1', '#FFFFFF'),
    textPrimary: readThemeColor('--text-primary', '#101828'),
    textSecondary: readThemeColor('--text-secondary', '#475467'),
    textMuted: readThemeColor('--text-muted', '#98A2B3'),
  }), [theme]);

  return { colors };
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export type GainsMonitorView = 'overview' | 'trends' | 'roi' | 'alerts';

export const GainsMonitorDashboard: React.FC<{
  organizationId: string;
  projectId?: string;
  view?: GainsMonitorView;
  onSelectedKaizenChange?: (kaizen: { id: string; projectId: string | null; title: string } | null) => void;
  measurementRequest?: number;
}> = ({ organizationId, projectId, view = 'overview', onSelectedKaizenChange, measurementRequest = 0 }) => {
  const [showSnapshotForm, setShowSnapshotForm] = useState(false);
  const [snapshotKaizen, setSnapshotKaizen] = useState<KaizenRecord | null>(null);
  const seenMeasurementRequest = useRef(measurementRequest);
  
  const [dashboardData, setDashboardData] = useState<GainsDashboardData>({
    total_kaizens: 0,
    active_kaizens: 0,
    sustained_kaizens: 0,
    regressed_kaizens: 0,
    total_lead_time_reduction: null,
    total_cost_savings: null,
    total_fte_freed: null,
    avg_sustainability_rate: 0,
    avg_regression_time: 0,
    active_alerts: [],
    critical_alerts_count: 0
  });
  const [kaizens, setKaizens] = useState<KaizenRecord[]>([]);
  const [selectedKaizen, setSelectedKaizen] = useState<KaizenRecord | null>(null);
  const [trendData, setTrendData] = useState<TrendChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertsRead, setAlertsRead] = useState<OpenAlertsRead | null>(null);
  const [roi, setRoi] = useState<KaizenROI | null | undefined>(undefined);

  // Carregar dados
  useEffect(() => {
    loadData();
  }, [organizationId, projectId]);

  const loadData = async () => {
    setLoading(true);
    
    const [dashboard, kaizenList] = await Promise.all([
      GainsMonitorService.getDashboardData(organizationId),
      // 🔒 FIX: Filtrar kaizens por organização e projeto para isolamento multi-cliente
      GainsMonitorService.getKaizens({ organization_id: organizationId, project_id: projectId })
    ]);
    


    setDashboardData(dashboard);
    setKaizens(kaizenList);
    
    // Selecionar primeiro Kaizen automaticamente
    if (kaizenList.length > 0 && !selectedKaizen) {
      selectKaizen(kaizenList[0]);
    }

    setLoading(false);
  };

  const selectKaizen = async (kaizen: KaizenRecord) => {
    setSelectedKaizen(kaizen);
    const trend = await GainsMonitorService.getTrendData(kaizen.id);
    setTrendData(trend);
  };

  useEffect(() => {
    onSelectedKaizenChange?.(selectedKaizen ? {
      id: selectedKaizen.id,
      projectId: selectedKaizen.project_id || null,
      title: selectedKaizen.title,
    } : null);
  }, [onSelectedKaizenChange, selectedKaizen]);

  useEffect(() => {
    if (view !== 'alerts') return;
    let active = true;
    setAlertsRead(null);
    GainsMonitorService.getActiveAlerts().then((result) => {
      if (!active) return;
      setAlertsRead(result);
    });
    return () => { active = false; };
  }, [view, organizationId]);

  useEffect(() => {
    if (view !== 'roi' || !selectedKaizen) {
      setRoi(undefined);
      return;
    }
    let active = true;
    setRoi(undefined);
    GainsMonitorService.getROI(selectedKaizen.id).then((value) => {
      if (active) setRoi(value);
    });
    return () => { active = false; };
  }, [view, selectedKaizen]);

  useEffect(() => {
    if (measurementRequest === seenMeasurementRequest.current || !selectedKaizen) return;
    seenMeasurementRequest.current = measurementRequest;
    setSnapshotKaizen(selectedKaizen);
    setShowSnapshotForm(true);
  }, [measurementRequest, selectedKaizen]);

  if (loading) {
    return (
      <div className="enterprise-page h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[var(--brand-primary)]"></div>
          <p className="enterprise-text-secondary">Carregando Monitor de Ganhos...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="enterprise-page text-center">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 enterprise-text-muted" />
        <p className="text-gray-600 dark:text-slate-400">Nenhum dado disponível</p>
      </div>
    );
  }

  const openSnapshot = (kaizen: KaizenRecord) => {
    setSnapshotKaizen(kaizen);
    setShowSnapshotForm(true);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden p-3">
      {view === 'overview' && (
        <div className="grid grid-cols-2 items-start gap-2 xl:grid-cols-4">
          <MetricCard
            title="Benefício anual ativo estimado"
            value={GainsMonitorService.formatCurrency(dashboardData.total_cost_savings)}
            subtitle="Monitoramento e sustentação"
          />
          <MetricCard
            title="Lead Time reduzido"
            value={GainsMonitorService.formatTime(dashboardData.total_lead_time_reduction)}
            subtitle="Baseline contra medição atual"
          />
          <MetricCard
            title="Capacidade liberada"
            value={Number.isFinite(dashboardData.total_fte_freed) ? `${dashboardData.total_fte_freed.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} FTE` : '—'}
            subtitle="Recursos disponíveis"
          />
          <MetricCard
            title="Resultados em risco"
            value={String(dashboardData.regressed_kaizens)}
            subtitle={`${dashboardData.critical_alerts_count} ${dashboardData.critical_alerts_count === 1 ? 'alerta crítico' : 'alertas críticos'}`}
          />
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 xl:grid-cols-[minmax(200px,26%)_minmax(0,1fr)]">
        <KaizenListPanel
          kaizens={kaizens}
          selectedKaizen={selectedKaizen}
          onSelectKaizen={selectKaizen}
        />

        <div className="min-h-0 min-w-0 space-y-2 overflow-y-auto pb-8 pr-1">
          {view === 'alerts' ? (
            alertsRead ? <AlertsPanel read={alertsRead} /> : <p className="enterprise-card-surface p-4 text-sm enterprise-text-secondary">Carregando alertas...</p>
          ) : view === 'roi' ? (
            <RoiPanel kaizen={selectedKaizen} roi={roi} />
          ) : !selectedKaizen || !trendData ? (
            <div className="enterprise-card-surface flex h-40 items-center justify-center enterprise-text-muted">
              <div className="text-center">
                <Target className="mx-auto mb-2 h-8 w-8 opacity-50" />
                <p className="text-sm">Selecione um Kaizen para ver detalhes</p>
              </div>
            </div>
          ) : (
            <>
              <SelectedKaizenHeader kaizen={selectedKaizen} trendData={trendData} />
              {/* Dívida 3D: Tendências repete este gráfico. A aba permanece até a análise de estabilidade. */}
              {(view === 'overview' || view === 'trends') && (
                <TrendChart
                  kaizen={selectedKaizen}
                  trendData={trendData}
                  onAddSnapshot={openSnapshot}
                />
              )}
              {view === 'overview' && (
                <>
                  <KaizenResultSection kaizenId={selectedKaizen.id} />
                  <StatusCounters kaizens={kaizens} />
                  <LinkedActions kaizen={selectedKaizen} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal: SnapshotForm */}
      {showSnapshotForm && snapshotKaizen && (
        <SnapshotForm
          kaizen={snapshotKaizen}
          onClose={() => {
            setShowSnapshotForm(false);
            setSnapshotKaizen(null);
          }}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

/**
 * Card de Métrica
 */
const STATUS_LABEL: Record<string, string> = {
  planejado: 'Planejado',
  em_implementacao: 'Em implementação',
  implementado: 'Implementado',
  em_monitoramento: 'Em monitoramento',
  sustentado: 'Sustentado',
  regredido: 'Regredido',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

function hasMeasurement(kaizen: KaizenRecord): boolean {
  return [
    kaizen.current_lead_time,
    kaizen.current_cycle_time,
    kaizen.current_ftr,
    kaizen.current_capacity_fte,
  ].some((value) => typeof value === 'number' && Number.isFinite(value));
}

function formatDay(value: Date | string | null | undefined): string {
  if (value == null) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

const MetricCard: React.FC<{
  title: string;
  value: string;
  subtitle: string;
}> = ({ title, value, subtitle }) => (
  <div className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
    <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-muted)]">{title}</div>
    <div className="mt-0.5 text-lg font-semibold leading-tight text-[var(--text-primary)]">{value}</div>
    <div className="text-[10px] text-[var(--text-secondary)]">{subtitle}</div>
  </div>
);

/**
 * Lista de Kaizens
 */
const KaizenListPanel: React.FC<{
  kaizens: KaizenRecord[];
  selectedKaizen: KaizenRecord | null;
  onSelectKaizen: (kaizen: KaizenRecord) => void;
}> = ({ kaizens, selectedKaizen, onSelectKaizen }) => {
  return (
    <div className="flex max-h-72 min-h-0 flex-col overflow-hidden rounded border border-[var(--border-default)] bg-[var(--bg-surface)] xl:h-full xl:max-h-none">
      <h2 className="border-b border-[var(--border-default)] px-3 py-2 text-[11px] font-semibold text-[var(--text-primary)]">
        Kaizens ({kaizens.length})
      </h2>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {kaizens.map((kaizen) => {
          const isSelected = selectedKaizen?.id === kaizen.id;
          return (
            <button
              key={kaizen.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectKaizen(kaizen)}
              className={`w-full rounded border px-2 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-1 ${
                isSelected
                  ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]'
                  : 'border-transparent hover:bg-[var(--bg-surface-2)]'
              }`}
            >
              <div className={`truncate text-[12px] text-[var(--text-primary)] ${isSelected ? 'font-semibold' : 'font-medium'}`}>{kaizen.title}</div>
              <div className="truncate text-[10px] text-[var(--text-secondary)]">{kaizen.responsible || '—'}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className="rounded bg-[var(--bg-surface)] px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)]">
                  {statusLabel(kaizen.status)}
                </span>
                <span className="rounded bg-[var(--bg-surface)] px-1.5 py-0.5 text-[10px] text-[var(--text-muted)]">
                  {hasMeasurement(kaizen) ? 'Medição registrada' : 'Sem medição atual'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Gráfico de Tendência de Lead Time
 */
const TrendChart: React.FC<{
  kaizen: KaizenRecord;
  trendData: TrendChartData;
  onAddSnapshot: (kaizen: KaizenRecord) => void;
}> = ({ kaizen, trendData, onAddSnapshot }) => {
  const { colors: COLORS } = useGainsColors();
  
  const finiteTarget = typeof trendData.target === 'number' && Number.isFinite(trendData.target);
  const chartData = [
    ...(typeof trendData.baseline === 'number' && Number.isFinite(trendData.baseline) ? [{
      date: 'Baseline',
      value: trendData.baseline,
      dateSort: 0,
    }] : []),
    ...trendData.snapshots.map((s) => ({
      date: new Date(s.date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      }),
      value: s.value,
      dateSort: new Date(s.date).getTime(),
    })),
  ].sort((a, b) => a.dateSort - b.dateSort);

  const hasMultipleSnapshots = trendData.snapshots.length >= 2;
  const hasSingleSnapshot = trendData.snapshots.length === 1;

  return (
    <div className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-[12px] font-semibold text-[var(--text-primary)]">Tendência de Lead Time</h2>
            {/* Status operacional. Não equivale a uma condição aberta em regression_alerts. */}
            {kaizen.status === 'regredido' && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-200">
                <AlertTriangle className="h-3 w-3" />
                Regressão detectada
              </span>
            )}
            {kaizen.status === 'sustentado' && (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" />
                Sustentado
              </span>
            )}
          </div>
          <p className="text-[10px] text-[var(--text-secondary)]">
            Baseline {GainsMonitorService.formatTime(trendData.baseline)} · Meta {GainsMonitorService.formatTime(trendData.target)}
            {hasSingleSnapshot ? ' · primeira medição registrada' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onAddSnapshot(kaizen)}
          className="inline-flex shrink-0 items-center gap-1 rounded bg-[var(--brand-primary)] px-2 py-1 text-[11px] font-semibold text-white"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar medição
        </button>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.15)" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 11, fill: 'var(--text-secondary, #64748b)' }}
            stroke="rgba(148, 163, 184, 0.35)"
            angle={-15}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: 'var(--text-secondary, #64748b)' }}
            stroke="rgba(148, 163, 184, 0.35)"
            label={{ 
              value: 'Lead Time (min)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fontSize: 12, fill: 'var(--text-secondary, #64748b)' }
            }}
          />
          
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-surface, white)',
              border: '1px solid var(--border-default, #e5e7eb)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--text-primary, #0f172a)',
            }}
            formatter={(value: number) => [
              GainsMonitorService.formatTime(value),
              'Lead Time'
            ]}
            labelFormatter={(label) => `Data: ${label}`}
          />
          
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          
          {/* Linha do Target */}
          {finiteTarget && (
            <ReferenceLine
              y={trendData.target}
              stroke={COLORS.success}
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: 'Meta',
                position: 'right',
                fill: COLORS.success,
                fontSize: 11,
              }}
            />
          )}
          
          {/* Área de valores */}
          <Area
            type="monotone"
            dataKey="value"
            stroke={COLORS.primary}
            strokeWidth={hasMultipleSnapshots ? 3 : 0}
            fill="url(#colorValue)"
            name="Lead Time Atual"
            dot={{ 
              r: 5, 
              fill: COLORS.primary, 
              strokeWidth: 2, 
              stroke: 'var(--bg-surface, #fff)' 
            }}
            activeDot={{ r: 7 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Legenda informativa */}
      {trendData.snapshots.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-xs text-gray-600 dark:text-slate-400">
          <div className="flex items-center gap-4">
            <span>
              <strong>{trendData.snapshots.length}</strong> {trendData.snapshots.length === 1 ? 'medição' : 'medições'} registradas
            </span>
            {hasMultipleSnapshots && (
              <span className="text-green-600 dark:text-green-400 font-medium">
                ✓ Evolução sendo monitorada
              </span>
            )}
          </div>
          <span className="text-[var(--text-muted)]">Atual {GainsMonitorService.formatTime(kaizen.current_lead_time)}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Painel de Detalhes do Kaizen
 */
const SelectedKaizenHeader: React.FC<{
  kaizen: KaizenRecord;
  trendData: TrendChartData;
}> = ({ kaizen, trendData }) => {
  const latest = [...trendData.snapshots].sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime())[0];
  const typeLabel = KAIZEN_IMPROVEMENT_TYPE_LABEL[kaizen.type] ?? kaizen.type.replace(/_/g, ' ');
  return (
    <div className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">{kaizen.title}</h2>
        <span className="rounded bg-[var(--bg-surface-2)] px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)]">
          {statusLabel(kaizen.status)}
        </span>
        <SelectedResultBadge kaizenId={kaizen.id} />
      </div>
      <p className="mt-1 text-[11px] text-[var(--text-secondary)]">
        Responsável: {kaizen.responsible || '—'}
        {' · '}Tipo: {typeLabel}
      </p>
      <p className="text-[11px] text-[var(--text-secondary)]">
        Implementado em: {formatDay(kaizen.implementation_date)}
        {' · '}Última medição: {latest ? formatDay(latest.date) : '—'}
      </p>
    </div>
  );
};

function SelectedResultBadge({ kaizenId }: { kaizenId: string }) {
  const review = useEligibleKaizenSnapshots(kaizenId);
  const validations = useKaizenResultValidations(kaizenId);
  const kaizen = review.data?.kaizen;
  if (!kaizen || review.isLoading || validations.isLoading) return null;
  const gate = resultUiState({
    kaizen,
    snapshots: review.data?.snapshots ?? [],
    latestValidation: validations.data?.[0] ?? null,
  });
  const tone = gate.state === 'validated'
    ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
    : gate.state === 'awaiting' || gate.state === 'rejected'
      ? 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
      : 'bg-[var(--bg-surface-2)] text-[var(--text-muted)]';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}>
      {RESULT_UI_LABEL[gate.state]}
    </span>
  );
}

const StatusCounters: React.FC<{ kaizens: KaizenRecord[] }> = ({ kaizens }) => {
  const counts = kaizens.reduce<Record<string, number>>((acc, kaizen) => {
    acc[kaizen.status] = (acc[kaizen.status] || 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(counts);
  return (
    <div className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
      <h3 className="text-[11px] font-semibold text-[var(--text-primary)]">Distribuição por status</h3>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {entries.length === 0 && <span className="text-[11px] text-[var(--text-muted)]">—</span>}
        {entries.map(([status, count]) => (
          <span key={status} className="inline-flex items-center gap-1 rounded border border-[var(--border-default)] bg-[var(--bg-surface-2)] px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)]">
            {statusLabel(status)}
            <strong className="font-semibold text-[var(--text-primary)]">{count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

const LinkedActions: React.FC<{ kaizen: KaizenRecord }> = ({ kaizen }) => {
  const getActions = useProjectStore(s => s.getActions);
  const linkedActions = (kaizen.linked_action_ids || []).map(id => getActions().find(a => a.id === id)).filter(Boolean);
  if (linkedActions.length === 0) return null;
  return (
    <div className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
      <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-[var(--text-primary)]">
        <Link2 className="h-3 w-3" />
        Ações vinculadas ({linkedActions.length})
      </div>
      <div className="space-y-0.5">
        {linkedActions.map((action) => (
          <div key={action!.id} className="truncate text-[11px] text-[var(--text-secondary)]">{action!.title}</div>
        ))}
      </div>
    </div>
  );
};

const RoiPanel: React.FC<{
  kaizen: KaizenRecord | null;
  roi: KaizenROI | null | undefined;
}> = ({ kaizen, roi }) => {
  if (!kaizen) {
    return <p className="enterprise-card-surface p-4 text-sm enterprise-text-secondary">Selecione um Kaizen para visualizar o ROI.</p>;
  }
  if (roi === undefined) {
    return <p className="enterprise-card-surface p-4 text-sm enterprise-text-secondary">Carregando ROI...</p>;
  }
  if (!roi) {
    return <p className="enterprise-card-surface p-4 text-sm enterprise-text-secondary">Este Kaizen ainda não tem ROI registrado.</p>;
  }
  const inclusion = benefitInclusion(kaizen.status);
  const finance = canonicalAnnualFinance(roi.annual_cost_saved, roi.total_investment);
  const rows = [
    ['Investimento', GainsMonitorService.formatCurrency(finance.investment)],
    [annualBenefitLabel(inclusion), GainsMonitorService.formatCurrency(finance.annualBenefit)],
    ['Payback', formatPaybackMonths(finance.paybackMonths)],
    ['ROI anual', formatCanonicalPercent(finance.roiPercent)],
  ];
  return (
    <div className="enterprise-card-surface p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold enterprise-text-primary">ROI de {kaizen.title}</h3>
        <span className="rounded bg-[var(--bg-surface)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
          {benefitClassLabel(inclusion)}
        </span>
      </div>
      <p className="mb-3 text-[11px] enterprise-text-secondary">{inclusionNote(inclusion, kaizen.status)}</p>
      <dl className="grid grid-cols-2 gap-3 text-xs">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="enterprise-text-muted">{label}</dt>
            <dd className="font-medium enterprise-text-primary">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/**
 * Painel de Alertas
 */
const AlertsPanel: React.FC<{
  read: OpenAlertsRead;
}> = ({ read }) => {
  const headline = openAlertsHeadline(read);
  const detail = openAlertsDetail(read);
  const conditions = read.status === 'ok' ? read.conditions : [];
  return (
    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-red-200 dark:border-red-800/40 p-3">
      <h3 className="text-sm font-bold text-red-900 dark:text-red-300 mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4" />
        {headline}
      </h3>
      {detail && (
        <p className={`mb-2 text-[11px] ${read.status === 'error' ? 'text-red-800 dark:text-red-200' : 'text-gray-600 dark:text-slate-400'}`}>
          {detail}
        </p>
      )}

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {conditions.map(alert => (
          <div
            key={alert.id}
            className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800/40 rounded"
          >
            <div className="flex items-start justify-between mb-2">
              <span className="font-medium text-red-900 dark:text-red-200 text-sm">
                {alert.metric_name.replace('_', ' ').toUpperCase()}
              </span>
              <span className="text-xs text-red-700 dark:text-red-300 font-medium">
                +{alert.deviation_percentage.toFixed(1)}%
              </span>
            </div>
            
            <div className="text-xs text-red-700 dark:text-red-300">
              Target: {alert.target_value} → Atual: {alert.current_value}
            </div>
            
            <div className="text-xs text-gray-600 dark:text-slate-400 mt-2">
              Detectado em {new Date(alert.detected_at).toLocaleDateString('pt-BR')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GainsMonitorDashboard;
